/**
 * Story → Tests authoring contracts. Shared between
 * `packages/api/src/routes/test-authoring.ts` and
 * `packages/flow-builder/src/test-authoring/api-client.ts`.
 *
 * Storage / persistence shapes:
 *   - StoryLink           → row in `story_links`
 *   - ManualTestCase      → row in `manual_test_cases`
 *
 * In-flight (non-persistent) shapes:
 *   - NormalizedStory     → connector output, what the LLM sees
 *   - ProposedManualCase  → LLM output before the user reviews/edits
 *   - AutomationDraft     → LLM output for "automate this", before
 *                           IntentEngine.parse turns it into FlowSteps
 *
 * Route I/O envelopes are at the bottom of this file.
 */

import { z } from 'zod';
import { QUALITY_RISK_DOMAINS, QUALITY_RISK_SEVERITIES } from './quality-hq';

// ---------------------------------------------------------------------------
// Source taxonomy. Mirrors story_links.source / source_kind enum
// values in migration 037.
// ---------------------------------------------------------------------------

export const StorySourceSchema = z.enum(['paste', 'jira', 'ado', 'rally', 'pdf', 'image', 'audio', 'prd']);
export type StorySource = z.infer<typeof StorySourceSchema>;

/** KaneAI-parity multi-format ingest kinds (subset of StorySource). */
export const StoryIngestKindSchema = z.enum(['pdf', 'image', 'audio', 'prd']);
export type StoryIngestKind = z.infer<typeof StoryIngestKindSchema>;

export const ManualCaseTypeSchema = z.enum(['happy', 'edge', 'negative', 'security', 'perf', 'accessibility']);
export type ManualCaseType = z.infer<typeof ManualCaseTypeSchema>;

export const ManualCasePrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type ManualCasePriority = z.infer<typeof ManualCasePrioritySchema>;

export const ManualCaseStatusSchema = z.enum(['draft', 'approved', 'deprecated']);
export type ManualCaseStatus = z.infer<typeof ManualCaseStatusSchema>;

export const ManualCaseSourceSchema = z.enum(['ai', 'user', 'edited']);
export type ManualCaseSource = z.infer<typeof ManualCaseSourceSchema>;

// ---------------------------------------------------------------------------
// Manual test case step shape. Deliberately prose-y (`action` and
// optional `data`) — manual cases are read by humans, not executed by
// a runner. The automation handoff (`AutomationDraft`) is a separate
// schema.
// ---------------------------------------------------------------------------

export const ManualStepSchema = z.object({
  action: z.string().min(1),
  data: z.string().optional(),
});
export type ManualStep = z.infer<typeof ManualStepSchema>;

// ---------------------------------------------------------------------------
// What a connector returns / what the LLM operates on.
// ---------------------------------------------------------------------------

export const NormalizedStorySchema = z.object({
  source: StorySourceSchema,
  // `source_url` is what the user pasted (https://...). null for paste.
  sourceUrl: z.string().url().nullable(),
  // Canonical reference (PROJ-123, AB#456, US12345). For paste-only
  // stories we synthesise a slug from the title.
  sourceRef: z.string().min(1),
  title: z.string().min(1),
  description: z.string().default(''),
  acceptanceCriteria: z.array(z.string()).default([]),
  // External links the connector found inside the story (designs,
  // mocks, dependent tickets). We surface them to the user but don't
  // fetch them — out of scope for MVP.
  links: z.array(z.string().url()).default([]),
  // Optional project key from the upstream system (for round-tripping
  // status updates later).
  projectKey: z.string().optional(),
});
export type NormalizedStory = z.infer<typeof NormalizedStorySchema>;

// ---------------------------------------------------------------------------
// Persisted shapes.
// ---------------------------------------------------------------------------

export const StoryLinkSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  source: StorySourceSchema,
  sourceUrl: z.string().nullable(),
  sourceRef: z.string(),
  title: z.string(),
  description: z.string(),
  acceptanceCriteria: z.array(z.string()),
  fetchedAt: z.string(),
  createdBy: z.string().uuid().nullable(),
});
export type StoryLink = z.infer<typeof StoryLinkSchema>;

export const ManualTestCaseSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  storyLinkId: z.string().uuid().nullable(),
  title: z.string(),
  caseType: ManualCaseTypeSchema,
  priority: ManualCasePrioritySchema,
  preconditions: z.array(z.string()),
  steps: z.array(ManualStepSchema),
  expectedResults: z.array(z.string()),
  tags: z.array(z.string()),
  status: ManualCaseStatusSchema,
  source: ManualCaseSourceSchema,
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ManualTestCase = z.infer<typeof ManualTestCaseSchema>;

// ---------------------------------------------------------------------------
// LLM output shapes — pre-review.
//
// `ProposedManualCase` is the same shape the user reviews in the
// wizard's review step; their edits / strikes mutate this list before
// the commit call.
//
// `AutomationDraft.intentText` is one prose line per draft. We feed
// it through `IntentEngine.parse()` server-side to get canvas-ready
// `FlowStep[]`; the wizard never sees the parsed flow until commit.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Confidence tier — banding for the per-row chip in the Review step. The
// thresholds are kept here (not in the UI) so server-side tests + the
// frontend agree on the same bucket boundaries.
// ---------------------------------------------------------------------------

export const ConfidenceTierSchema = z.enum(['high', 'med', 'low']);
export type ConfidenceTier = z.infer<typeof ConfidenceTierSchema>;

export const CONFIDENCE_TIER_THRESHOLDS = {
  high: 0.8,
  med: 0.5,
} as const;

export function deriveConfidenceTier(confidence: number): ConfidenceTier {
  if (confidence >= CONFIDENCE_TIER_THRESHOLDS.high) return 'high';
  if (confidence >= CONFIDENCE_TIER_THRESHOLDS.med) return 'med';
  return 'low';
}

// "Original" snapshot a row was first generated with — used for the diff
// drawer's "revert to original" and to render the line-level diff. Mirrors
// the editable subset of ProposedManualCase / AutomationDraft, never the
// derived fields (confidence / tier).
export const ManualCaseOriginalSchema = z.object({
  title: z.string(),
  caseType: ManualCaseTypeSchema,
  priority: ManualCasePrioritySchema,
  preconditions: z.array(z.string()),
  steps: z.array(ManualStepSchema),
  expectedResults: z.array(z.string()),
  tags: z.array(z.string()),
});
export type ManualCaseOriginal = z.infer<typeof ManualCaseOriginalSchema>;

export const AutomationDraftOriginalSchema = z.object({
  title: z.string(),
  intentText: z.string(),
  coverageFor: z.array(z.string()),
});
export type AutomationDraftOriginal = z.infer<typeof AutomationDraftOriginalSchema>;

export const ProposedManualCaseSchema = z.object({
  // Client-side stable id; not a UUID until commit.
  tempId: z.string().min(1),
  title: z.string().min(1),
  caseType: ManualCaseTypeSchema,
  priority: ManualCasePrioritySchema,
  preconditions: z.array(z.string()).default([]),
  steps: z.array(ManualStepSchema).default([]),
  expectedResults: z.array(z.string()).default([]),
  tags: z.array(z.string()).default([]),
  // The wizard can mark cases for automation. Default off — user opts
  // in per row.
  automate: z.boolean().default(false),
  // Normalised LLM confidence in [0, 1]. Default 0.5 so v0 rows that
  // never set the field land in the `med` tier and continue to work.
  confidence: z.number().min(0).max(1).default(0.5),
  // Pristine LLM output. Frozen at first generate; the wizard never
  // mutates it (only `revert` reads from it). Optional so v0 callers
  // still work.
  originalProposal: ManualCaseOriginalSchema.optional(),
});
export type ProposedManualCase = z.infer<typeof ProposedManualCaseSchema>;

export const AutomationDraftSchema = z.object({
  tempId: z.string().min(1),
  title: z.string().min(1),
  // Natural-language description that IntentEngine.parse will turn
  // into FlowStep[]. The LLM produces this, the user can edit it.
  intentText: z.string().min(1),
  // Titles of the manual cases this automation covers. UI-only;
  // helps the reviewer see "this flow covers happy + edge".
  coverageFor: z.array(z.string()).default([]),
  // Normalised LLM confidence in [0, 1]. Same semantics as
  // ProposedManualCase.confidence.
  confidence: z.number().min(0).max(1).default(0.5),
  originalProposal: AutomationDraftOriginalSchema.optional(),
});
export type AutomationDraft = z.infer<typeof AutomationDraftSchema>;

// LLM raw output — the generator validates this shape after a
// JSON.parse on the response, before exposing it to the UI as a
// `ProposedManualCase` / `AutomationDraft` list (which add tempIds).
//
// The `confidence` field is optional on the wire so an LLM that doesn't
// emit it (older provider models, BYOK setups without prompt-update)
// still validates. The generator falls back to a heuristic when the
// field is absent. `covered_ac_indices` lets the LLM declare which AC
// (by 0-based index) each case maps onto; the generator uses this to
// derive a Rynora-normalised confidence = llm_conf × matched_AC_ratio.
// ---------------------------------------------------------------------------
// `pending-quality-hq` v0.1 — Story → risks.
//
// A story's first artefact is the set of business risks it puts at
// stake, plus the evidence we recommend to hold each one down. Cases
// are the follow-up ("Lock these risks with cases"), not the opening
// move. Accepted rows are persisted as `quality_risks` on commit with
// `source = 'proposed_from_story'`.
// ---------------------------------------------------------------------------

export const ProposedQualityRiskSchema = z.object({
  tempId: z.string(),
  title: z.string().min(1).max(160),
  whyItHurts: z.string().max(500),
  severity: z.enum(QUALITY_RISK_SEVERITIES),
  domain: z.enum(QUALITY_RISK_DOMAINS),
  /** Plain-English evidence we recommend, e.g. "Manual cycle on the saved-card path". */
  evidencePlan: z.array(z.string()).default([]),
  /** 0-based indices of the story's acceptance criteria this risk covers. */
  coveredAcIndices: z.array(z.number().int().min(0)).default([]),
  /** Review-step toggle. Only kept rows reach `quality_risks`. */
  keep: z.boolean().default(true),
});
export type ProposedQualityRisk = z.infer<typeof ProposedQualityRiskSchema>;

export const GeneratedTestsSchema = z.object({
  // Optional on the wire so a BYOK model that ignores the risks block
  // still validates; the generator falls back to a deterministic
  // derivation from the story itself.
  risks: z
    .array(
      z.object({
        title: z.string(),
        why_it_hurts: z.string().default(''),
        severity: z.enum(QUALITY_RISK_SEVERITIES).default('high'),
        domain: z.enum(QUALITY_RISK_DOMAINS).default('revenue'),
        evidence_plan: z.array(z.string()).default([]),
        covered_ac_indices: z.array(z.number().int().min(0)).default([]),
      }),
    )
    .default([]),
  manual: z.array(
    z.object({
      title: z.string(),
      case_type: ManualCaseTypeSchema,
      priority: ManualCasePrioritySchema,
      preconditions: z.array(z.string()),
      steps: z.array(ManualStepSchema),
      expected_results: z.array(z.string()),
      tags: z.array(z.string()).default([]),
      confidence: z.number().min(0).max(1).optional(),
      covered_ac_indices: z.array(z.number().int().min(0)).optional(),
    }),
  ),
  automation: z.array(
    z.object({
      title: z.string(),
      intent_text: z.string(),
      coverage_for: z.array(z.string()).default([]),
      confidence: z.number().min(0).max(1).optional(),
    }),
  ),
});
export type GeneratedTests = z.infer<typeof GeneratedTestsSchema>;

// ---------------------------------------------------------------------------
// Generation options — surfaced in the wizard's Options step.
// ---------------------------------------------------------------------------

export const GenerationOptionsSchema = z.object({
  // 'standard' = ~1-2 cases per AC clause, balanced across types.
  // 'thorough' = ~3-4 cases per AC clause, more edge / negative coverage.
  coverage: z.enum(['standard', 'thorough']).default('standard'),
  /**
   * @deprecated Phase 8.9 — Story → Automation UX removed. Wire field
   * retained for backward-compatible clients; generate always returns
   * `automation: []` and commit ignores automation[]. Default false.
   * Use Scenario → Convert for Flows.
   */
  includeAutomation: z.boolean().default(false),
  // Include the full description in the LLM prompt. Off for paste-text
  // where the description was already noisy.
  includeDescription: z.boolean().default(true),
  // `pending-quality-hq` v0.1 — ask for business risks as the leading
  // artefact. On by default: the risk list is what the wizard shows
  // first. Off reproduces the pre-v0.1 cases-only response.
  includeRisks: z.boolean().default(true),
});
export type GenerationOptions = z.infer<typeof GenerationOptionsSchema>;

// ---------------------------------------------------------------------------
// Route I/O envelopes.
// ---------------------------------------------------------------------------

// POST /api/projects/:projectId/test-authoring/fetch
export const FetchStoryRequestSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('paste'),
    text: z.string().min(1).max(50_000),
    title: z.string().min(1).max(500).optional(),
  }),
  z.object({
    source: z.literal('jira'),
    url: z.string().url(),
  }),
  z.object({
    source: z.literal('ado'),
    url: z.string().url().optional(),
    // For "AB#456" pastes the wizard can send just the ref + the
    // org's default project will fill in the rest.
    ref: z.string().min(1).optional(),
  }),
  z.object({
    source: z.literal('rally'),
    url: z.string().url().optional(),
    ref: z.string().min(1).optional(),
  }),
]);
export type FetchStoryRequest = z.infer<typeof FetchStoryRequestSchema>;

/** POST /api/projects/:projectId/test-authoring/ingest — PDF / image / audio / PRD. */
export const IngestStoryRequestSchema = z.object({
  source: StoryIngestKindSchema,
  filename: z.string().min(1).max(255),
  mimeType: z.string().max(200).optional(),
  contentBase64: z.string().min(1),
  title: z.string().min(1).max(500).optional(),
});
export type IngestStoryRequest = z.infer<typeof IngestStoryRequestSchema>;

export const FetchStoryResponseSchema = z.object({
  storyLinkId: z.string().uuid(),
  story: NormalizedStorySchema,
});
export type FetchStoryResponse = z.infer<typeof FetchStoryResponseSchema>;

// POST /api/projects/:projectId/test-authoring/generate
export const GenerateRequestSchema = z.object({
  storyLinkId: z.string().uuid(),
  options: GenerationOptionsSchema,
});
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

/** Compact acceptance snapshot wire (mirrors scenarios.ts; avoids import cycle). */
const ScenarioAcceptanceEvaluationSnapshotWireSchema = z.object({
  evaluatorVersion: z.string().trim().min(1).max(40),
  overall: z.number().min(0).max(1),
  structureScore: z.number().min(0).max(1),
  variantScore: z.number().min(0).max(1),
  traceabilityScore: z.number().min(0).max(1),
  groundingScore: z.number().min(0).max(1),
  hygieneScore: z.number().min(0).max(1),
  findingsSummary: z.object({
    highestSeverity: z.enum(['info', 'warn', 'block']),
    info: z.number().int().min(0),
    warn: z.number().int().min(0),
    block: z.number().int().min(0),
    codes: z.array(z.string()).max(30),
  }),
  groundingSummary: z.object({
    requirementCovered: z.boolean(),
    acceptanceCriteriaCovered: z.number().int().min(0),
    acceptanceCriteriaTotal: z.number().int().min(0),
    unsupportedClaims: z.number().int().min(0),
  }),
  convertReadyHint: z.boolean(),
  acceptedAt: z.string().min(1).max(40),
});

// `risks` is declared (and serialised) first — QH-15. The wizard reads
// the response top-down and leads with risk, never with a case count.
export const GenerateResponseSchema = z.object({
  risks: z.array(ProposedQualityRiskSchema).default([]),
  manual: z.array(ProposedManualCaseSchema),
  /**
   * Phase 8.5.7 — always empty from generate. Story/Jira must not propose
   * Flow creation; Automation is via Scenario convert only.
   */
  automation: z.array(AutomationDraftSchema),
  /**
   * True when the org opted out of AI: the story is still saved, but
   * nothing was proposed. The wizard points the user at Quality to
   * name risks by hand (QH-16).
   */
  aiSkipped: z.boolean().default(false),
  /**
   * Phase 8.5.7 — deterministic Scenario AI evaluation (same as generate-drafts).
   * Optional for older clients.
   */
  evaluation: z
    .object({
      overall: z.number().min(0).max(1),
      structureScore: z.number().min(0).max(1),
      variantScore: z.number().min(0).max(1),
      traceabilityScore: z.number().min(0).max(1),
      groundingScore: z.number().min(0).max(1),
      hygieneScore: z.number().min(0).max(1),
      findings: z
        .array(
          z
            .object({
              code: z.string(),
              severity: z.enum(['info', 'warn', 'block']),
              message: z.string(),
            })
            .passthrough(),
        )
        .max(100),
      grounding: z
        .object({
          requirementCovered: z.boolean(),
          acceptanceCriteriaCovered: z.number().int().min(0),
          acceptanceCriteriaTotal: z.number().int().min(0),
          unsupportedClaims: z.number().int().min(0),
          evidence: z.array(z.record(z.string(), z.unknown())).max(100),
        })
        .passthrough(),
      testabilityHint: z
        .object({
          suitability: z.enum(['manual', 'partial', 'automatable', 'unknown']),
          convertReadyHint: z.boolean(),
          summary: z.string(),
        })
        .passthrough(),
      convertReadyHint: z.boolean(),
      highestSeverity: z.enum(['info', 'warn', 'block']),
    })
    .optional(),
  /** Compact snapshot ready for CreateScenarioRequest.aiAcceptanceEvaluation. */
  acceptanceSnapshot: ScenarioAcceptanceEvaluationSnapshotWireSchema.optional(),
  generationSource: z.enum(['llm', 'heuristic']).optional(),
});
export type GenerateResponse = z.infer<typeof GenerateResponseSchema>;

// POST /api/projects/:projectId/test-authoring/commit
export const CommitRequestSchema = z.object({
  storyLinkId: z.string().uuid(),
  // Kept risks become `quality_risks` rows before any case is written,
  // so a commit that only accepts risks is a valid outcome.
  risks: z.array(ProposedQualityRiskSchema).default([]),
  manual: z.array(ProposedManualCaseSchema),
  /**
   * Phase 8.5.7 — ignored for Flow creation. Accepted for wire compat;
   * server never INSERT test_cases from Story/Jira commit.
   */
  automation: z.array(AutomationDraftSchema).default([]),
  /** Historical AI evaluation snapshot to persist on accepted Scenarios. */
  aiAcceptanceEvaluation: ScenarioAcceptanceEvaluationSnapshotWireSchema.optional(),
});
export type CommitRequest = z.infer<typeof CommitRequestSchema>;

export const CommitResponseSchema = z.object({
  riskIds: z.array(z.string().uuid()),
  manualIds: z.array(z.string().uuid()),
  /** Always empty after 8.5.7 — use Scenario → Automation convert. */
  automationFlowIds: z.array(z.string().uuid()),
  /** True when the client sent automation drafts that were not persisted as Flows. */
  automationCommitDeprecated: z.boolean().optional(),
  automationCommitMessage: z.string().max(500).optional(),
});
export type CommitResponse = z.infer<typeof CommitResponseSchema>;

// GET /api/projects/:projectId/manual-tests
export const ListManualTestsQuerySchema = z.object({
  caseType: ManualCaseTypeSchema.optional(),
  priority: ManualCasePrioritySchema.optional(),
  status: ManualCaseStatusSchema.optional(),
  storyLinkId: z.string().uuid().optional(),
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListManualTestsQuery = z.infer<typeof ListManualTestsQuerySchema>;

export const ListManualTestsResponseSchema = z.object({
  cases: z.array(ManualTestCaseSchema),
  total: z.number(),
});
export type ListManualTestsResponse = z.infer<typeof ListManualTestsResponseSchema>;

// PUT /api/projects/:projectId/manual-tests/:id
export const UpdateManualTestRequestSchema = ManualTestCaseSchema.partial().pick({
  title: true,
  caseType: true,
  priority: true,
  preconditions: true,
  steps: true,
  expectedResults: true,
  tags: true,
  status: true,
});
export type UpdateManualTestRequest = z.infer<typeof UpdateManualTestRequestSchema>;

// GET /api/projects/:projectId/manual-tests/export?format=...
export const ExportFormatSchema = z.enum(['csv', 'xlsx']);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

// ---------------------------------------------------------------------------
// Preview-run (dry-run before commit). The wizard's Review step uses this
// to give the reviewer a one-shot, non-persistent execution of an
// automation row so they can fix selector / data issues BEFORE committing
// the flow. The request carries the intent text (the same field the
// commit path feeds through IntentEngine.parse); the server parses,
// dispatches a single browser session, and returns the per-step verdict.
//
// **Not persisted.** A preview run does NOT add a row to `test_runs`. The
// result lives only in the HTTP response. A separate counter
// (`preview_run_count`, env-tunable cap) tracks abuse, surfaced via a
// `previewRunsRemaining` hint so the wizard can show a soft warning.
// ---------------------------------------------------------------------------

export const PreviewRunRequestSchema = z.object({
  // The natural-language description the LLM produced (or the user
  // edited). Same shape the commit endpoint accepts on AutomationDraft.
  intentText: z.string().min(1).max(10_000),
  // Optional draft title — surfaced in the response so a reviewer with
  // 3 preview tabs open can tell them apart.
  title: z.string().min(1).max(500).optional(),
  // Optional starting URL. If omitted the server uses the project's
  // baseUrl. The flow editor's existing precedence rules apply.
  startUrl: z.string().url().optional(),
  // Optional environment id (`environments` table). Provides cookies /
  // headers / variables for the dry-run; same precedence as a real run.
  environmentId: z.string().uuid().optional(),
});
export type PreviewRunRequest = z.infer<typeof PreviewRunRequestSchema>;

export const PreviewStepResultSchema = z.object({
  stepIndex: z.number().int().min(0),
  stepTitle: z.string(),
  // Mirrors the runner's status enum subset relevant for preview.
  status: z.enum(['passed', 'failed', 'skipped']),
  durationMs: z.number().int().min(0),
  // 1-line failure summary (no full stack — preview is for triage, not
  // post-mortem).
  errorSummary: z.string().nullable(),
});
export type PreviewStepResult = z.infer<typeof PreviewStepResultSchema>;

export const PreviewRunResponseSchema = z.object({
  // Inherited from the runner: 'passed' iff every step passed.
  overallStatus: z.enum(['passed', 'failed', 'errored']),
  totalDurationMs: z.number().int().min(0),
  steps: z.array(PreviewStepResultSchema),
  // 1-line human-readable summary for the wizard banner.
  summary: z.string(),
  // Soft-warn before the per-user / per-org cap is hit. -1 means the cap
  // is disabled (unlimited). 0 means the next call will 429.
  previewRunsRemaining: z.number().int(),
});
export type PreviewRunResponse = z.infer<typeof PreviewRunResponseSchema>;
